import { useContext, useState, useRef, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import usePageTitle from "../../../hooks/use-page-title";
import { usePortalSettingSet } from "../../../context/portal-context";
import { ROUTES } from "../../../routes";
import { MdDelete, MdModeEditOutline, MdSearch } from "react-icons/md";
import { Dropdown } from "react-bootstrap";
import Debouncer from "../../../modules/debouncer/debouncer";
import ServerSideGenericDataTable from "../../../components/common/ServerSideGenericDataTable";
import ApiGenericData from "../../../modules/api/generic-data";
import Notifier from "../../../modules/notifier/notifier";
import ApiTrait from "../../../modules/api/trait";
import ApiCohort from "../../../modules/api/cohort";
import ModulePopup from "../../../modules/popup/popup";
import AppUserContext from "../../../context/app-user-context";
import PagePortalNotFound from "../PagePortalNotFound/PagePortalNotFound";
import { HiArrowLeft } from "react-icons/hi";
import { FaFileExport } from "react-icons/fa";
import { BiImport } from "react-icons/bi";
import PopupImportTraits, {
  TPopupImportTraitsRef,
} from "../../../components/popup/PopupImportTraits";
import AlertLoading from "../../../components/common/AlertLoading";
import FooterBoxAction from "../../../components/common/FooterBoxAction";

export default function PageTrait() {
  const title = "Manage Trait";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const location = useLocation();
  const user = useContext(AppUserContext);
  const { programmeId, cohortId } = useParams();
  const isValidProgrammeId = user.programmeIds.includes(programmeId);
  const refPopupImportTraits = useRef<TPopupImportTraitsRef>();
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cohort, setCohort] = useState(null);
  const [tableData, setTableData] = useState(null);

  useEffect(() => {
    if (!isValidProgrammeId) return;
    ApiCohort.get(cohortId, true)
      .then((res) => {
        if (res.result === "OK" && res.data?.programme?._id === programmeId) {
          setCohort(res.data);
        } else if (res.message) Notifier.error(res.message);
      })
      .catch((error) => Notifier.error(error.message))
      .finally(() => setLoading(false));
  }, []);

  const onImportClick = () => {
    refPopupImportTraits.current.show();
  };

  const onExportClick = () => {
    ModulePopup.showAlertConfirm({
      title: "Confirmation",
      description: <p>Do you want to export all traits?</p>,
      onYes: exportTrait,
    });
  };

  const exportTrait = async () => {
    const { data = [], totalCount = 0 } = tableData;
    if (!totalCount) {
      Notifier.warn("There is no trait to export.");
      return;
    }
    let exportedTraits = [];
    if (totalCount === data.length) {
      exportedTraits = data;
    } else {
      const { status, message, data } = await ApiGenericData.getAll(
        `Trait`,
        cohortId
      );
      if (!status) {
        Notifier.error(message);
        return;
      }
      if (!data.length) {
        Notifier.warn("There is no trait to export.");
        return;
      }
      exportedTraits = data;
    }
    exportedTraits = exportedTraits.map((i) => {
      delete i._id;
      return i;
    });
    const blob = new Blob([JSON.stringify(exportedTraits)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${cohort.programme?.name ?? ""}_${cohort.name}_Traits.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isValidProgrammeId) {
    return <PagePortalNotFound />;
  }
  if (loading) {
    return <AlertLoading show={true} />;
  }
  if (!cohort) {
    return <div className="alert alert-danger">Cohort not found.</div>;
  }
  if (!cohort.pdMode) {
    return (
      <div className="alert alert-warning">
        Professional Development Mode is not enabled.
      </div>
    );
  }
  return (
    <main className="form-generic content-generic content-generic--fluid">
      <h1 className="fs-title hide-mobile-down">{title}</h1>
      <p>
        Cohort: <strong>{cohort.name}</strong>
        <br />
        Programme: <strong>{cohort.programme?.name ?? ""}</strong>
      </p>
      <div className="cg__actions">
        <div className="cg__action-buttons">
          {/* <Link
            to={`${ROUTES.PORTAL_COHORTS}/manage/${programmeId}/${cohortId}`}
            className="btn btn-primary"
            state={location.state}
          >
            <HiArrowLeft /> Back
          </Link> */}
          <Link
            to={`${ROUTES.PORTAL_TRAIT}/add/${programmeId}/${cohortId}`}
            className="btn btn-secondary"
            state={location.state}
          >
            Add New Trait
          </Link>{" "}
          <button
            className="btn trait-btn btn-import-trait"
            onClick={onImportClick}
          >
            <BiImport />
            Import{" "}
          </button>
          <button
            className="btn trait-btn btn-export-trait"
            onClick={onExportClick}
          >
            <FaFileExport /> Export
          </button>
          <button
            className="btn btn-outline-dark"
            onClick={() => setRefresh((i) => !i)}
          >
            Refresh
          </button>
        </div>
        <div className="cg__action-search">
          <span>
            <MdSearch />
          </span>
          <input
            type="text"
            placeholder="Search"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <PopupImportTraits
        ref={refPopupImportTraits}
        cohortId={cohortId}
        onImport={() => setRefresh((i) => !i)}
      />

      <ServerSideGenericDataTable
        refresh={refresh}
        api={ApiTrait.pagingSearch}
        cohortId={cohortId}
        search={search}
        columns={[
          {
            id: "name",
            name: "Name",
            cell: (row) => row.name,
          },
          {
            id: "description",
            name: "Description",
            cell: (row) => row.description,
          },
          {
            name: "",
            button: true,
            cell: (row) => (
              <div>
                <Dropdown align={"end"} drop={"down"}>
                  <Dropdown.Toggle
                    variant="secondary"
                    id="dropdown-basic"
                    className="btn-sm"
                  >
                    Manage
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Link
                      className="dropdown-item"
                      to={`${ROUTES.PORTAL_TRAIT}/edit/${programmeId}/${cohortId}/${row._id}`}
                      state={location.state}
                    >
                      <MdModeEditOutline className="cursor-pointer icon-action-default" /> Edit
                    </Link>

                    <Dropdown.Divider />

                    <button
                      className="dropdown-item btn-danger"
                      onClick={() => {
                        ModulePopup.showAlertConfirm({
                          title: "Delete Confirmation",
                          description: (
                            <>
                              <p>
                                Are you sure to delete this?
                                <br /> This action can't be undone. All data
                                that belongs to this trait will be broken.
                              </p>
                            </>
                          ),
                          onYes: () =>
                            Debouncer.execute("DELETE_ITEM", async () => {
                              ApiGenericData.remove(row._id).then((res) => {
                                if (res.status) {
                                  setRefresh((i) => !i);
                                  Notifier.success(res.message);
                                } else {
                                  Notifier.error(res.message);
                                }
                              });
                            }),
                        });
                      }}
                    >
                      <MdDelete /> Delete
                    </button>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ),
          },
        ]}
        getChildData={(data: any) => setTableData(data)}
      />

      <FooterBoxAction
        backUrl={`${ROUTES.PORTAL_COHORTS}/${programmeId}`}
        backUrlState={location.state}
      />
    </main>
  );
}
